/**
 * ArUco Marker Decoder
 *
 * Décod ce système détecte et décode les marqueurs ArUco dans une image.
 * Les marqueurs sont des grilles 7x7 (bordure noire + 5x5 intérieur encodé en Hamming).
 *
 * IDs des marqueurs:
 * - 0: Top-Left (TL)
 * - 1: Top-Right (TR)
 * - 2: Bottom-Left (BL)
 * - 3: Bottom-Right (BR)
 */

// Table de mots Hamming (5,2) - même table que dans arucoMarkers.ts
const HAMMING_WORDS: number[][] = [
  [1, 0, 0, 0, 0], // 00
  [1, 0, 1, 1, 1], // 01
  [0, 1, 0, 0, 1], // 10
  [0, 1, 1, 1, 0], // 11
];

export interface ArucoMarker {
  id: number; // 0=TL, 1=TR, 2=BL, 3=BR
  centerX: number;
  centerY: number;
  corners: { x: number; y: number }[]; // 4 coins du marqueur
  confidence: number; // Score de confiance (0-1)
}

export interface MarkerCandidate {
  x: number;
  y: number;
  size: number;
  isCandidate: boolean;
}

/**
 * Échantillonner une région et calculer la moyenne des niveaux de gris
 */
const sampleRegion = (
  imageData: ImageData,
  startX: number,
  startY: number,
  size: number
): number => {
  let sum = 0;
  let count = 0;

  const endX = Math.min(Math.floor(startX + size), imageData.width);
  const endY = Math.min(Math.floor(startY + size), imageData.height);

  for (let y = Math.max(0, Math.floor(startY)); y < endY; y++) {
    for (let x = Math.max(0, Math.floor(startX)); x < endX; x++) {
      const idx = (y * imageData.width + x) * 4;
      const r = imageData.data[idx];
      const g = imageData.data[idx + 1];
      const b = imageData.data[idx + 2];
      const gray = (r + g + b) / 3;
      sum += gray;
      count++;
    }
  }

  return count > 0 ? sum / count : 128;
};

/**
 * Extraire la grille 7x7 de pixels binarisés (0=blanc, 1=noir)
 */
export const extractGrid7x7 = (
  imageData: ImageData,
  startX: number,
  startY: number,
  size: number
): number[][] => {
  const grid: number[][] = [];
  const cellSize = size / 7;

  for (let row = 0; row < 7; row++) {
    const gridRow: number[] = [];
    for (let col = 0; col < 7; col++) {
      // Échantillonner au centre de chaque cellule
      const cellCenterX = startX + (col + 0.5) * cellSize;
      const cellCenterY = startY + (row + 0.5) * cellSize;

      // Moyenner les pixels dans un petit carré autour du centre (30% de la cellule)
      const sampleSize = cellSize * 0.3;
      const avgGray = sampleRegion(
        imageData,
        cellCenterX - sampleSize / 2,
        cellCenterY - sampleSize / 2,
        sampleSize
      );

      // Binariser avec seuil adaptatif
      gridRow.push(avgGray < 128 ? 1 : 0); // 1 = noir, 0 = blanc
    }
    grid.push(gridRow);
  }

  return grid;
};

/**
 * Valider que la bordure extérieure de la grille 7x7 est entièrement noire
 */
export const validateBlackBorder = (grid: number[][]): boolean => {
  if (grid.length !== 7 || grid[0].length !== 7) return false;

  // Première ligne (rang 0)
  if (!grid[0].every(cell => cell === 1)) return false;

  // Dernière ligne (rang 6)
  if (!grid[6].every(cell => cell === 1)) return false;

  // Première colonne (toutes les lignes)
  if (!grid.every(row => row[0] === 1)) return false;

  // Dernière colonne (toutes les lignes)
  if (!grid.every(row => row[6] === 1)) return false;

  return true;
};

/**
 * Décoder une ligne Hamming (5 bits → 2 bits de données)
 * Utilise la distance de Hamming pour trouver le mot le plus proche
 */
export const decodeHammingRow = (row: number[]): number[] | null => {
  if (row.length !== 5) return null;

  let minDistance = Infinity;
  let bestMatch = -1;

  for (let i = 0; i < HAMMING_WORDS.length; i++) {
    let distance = 0;
    for (let j = 0; j < 5; j++) {
      if (row[j] !== HAMMING_WORDS[i][j]) distance++;
    }

    if (distance < minDistance) {
      minDistance = distance;
      bestMatch = i;
    }
  }

  // Le code Hamming peut corriger 1 erreur, détecter 2 erreurs
  if (minDistance > 1) {
    return null; // Trop d'erreurs, rejet
  }

  // Convertir l'index (0,1,2,3) en 2 bits
  return [Math.floor(bestMatch / 2), bestMatch % 2];
};

/**
 * Convertir un tableau de bits en nombre
 */
export const bitsToNumber = (bits: number[]): number => {
  let result = 0;
  for (let i = 0; i < bits.length; i++) {
    result = (result << 1) | bits[i];
  }
  return result;
};

/**
 * Extraire les 4 coins d'un marqueur détecté
 */
const extractCorners = (
  grid: number[][],
  startX: number,
  startY: number,
  size: number
): { x: number; y: number }[] => {
  return [
    { x: startX, y: startY }, // TL
    { x: startX + size, y: startY }, // TR
    { x: startX + size, y: startY + size }, // BR
    { x: startX, y: startY + size }, // BL
  ];
};

/**
 * Calculer un score de confiance basé sur la qualité de la bordure noire
 */
const calculateConfidence = (grid: number[][]): number => {
  let blackBorderCount = 0;
  let totalBorderCells = 0;

  // Compter les cellules noires dans la bordure
  for (let i = 0; i < 7; i++) {
    // Première et dernière ligne
    if (grid[0][i] === 1) blackBorderCount++;
    if (grid[6][i] === 1) blackBorderCount++;
    totalBorderCells += 2;

    // Première et dernière colonne (sans compter les coins déjà comptés)
    if (i > 0 && i < 6) {
      if (grid[i][0] === 1) blackBorderCount++;
      if (grid[i][6] === 1) blackBorderCount++;
      totalBorderCells += 2;
    }
  }

  return blackBorderCount / totalBorderCells;
};

/**
 * Décoder un marqueur ArUco depuis une région d'image
 * Retourne null si le marqueur n'est pas valide
 */
export const decodeArucoMarker = (
  imageData: ImageData,
  x: number,
  y: number,
  size: number
): ArucoMarker | null => {
  // 1. Extraire la grille 7x7
  const grid = extractGrid7x7(imageData, x, y, size);

  // 2. Vérifier la bordure noire (critère de base pour un marqueur ArUco)
  if (!validateBlackBorder(grid)) {
    return null;
  }

  // 3. Extraire la grille intérieure 5x5 (enlever la bordure)
  const innerGrid = grid.slice(1, 6).map(row => row.slice(1, 6));

  // 4. Décoder chaque ligne avec Hamming
  const bits: number[] = [];
  for (let row = 0; row < 5; row++) {
    const rowBits = decodeHammingRow(innerGrid[row]);
    if (rowBits === null) {
      return null; // Erreur de décodage (trop de bruit)
    }
    bits.push(...rowBits); // Ajouter les 2 bits décodés
  }

  // 5. Reconstruire l'ID (10 bits → nombre)
  const id = bitsToNumber(bits);

  // 6. Vérifier que l'ID est valide (0, 1, 2, ou 3)
  if (id < 0 || id > 3) {
    return null; // ID invalide, pas un de nos marqueurs
  }

  // 7. Calculer le centre et les coins précis
  const corners = extractCorners(grid, x, y, size);
  const centerX = x + size / 2;
  const centerY = y + size / 2;

  return {
    id,
    centerX,
    centerY,
    corners,
    confidence: calculateConfidence(grid),
  };
};

/**
 * Vérifier qu'un quadrilatère est convexe
 * (tous les produits vectoriels doivent avoir le même signe)
 */
export const isConvexQuadrilateral = (
  points: { centerX: number; centerY: number }[]
): boolean => {
  if (points.length !== 4) return false;

  const crossProducts: number[] = [];

  for (let i = 0; i < 4; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % 4];
    const p3 = points[(i + 2) % 4];

    const v1x = p2.centerX - p1.centerX;
    const v1y = p2.centerY - p1.centerY;
    const v2x = p3.centerX - p2.centerX;
    const v2y = p3.centerY - p2.centerY;

    const cross = v1x * v2y - v1y * v2x;
    crossProducts.push(cross);
  }

  // Tous les produits vectoriels doivent avoir le même signe
  const allPositive = crossProducts.every(c => c > 0);
  const allNegative = crossProducts.every(c => c < 0);

  return allPositive || allNegative;
};

/**
 * Analyser une région pour déterminer si elle pourrait contenir un marqueur ArUco
 * (pré-filtrage rapide avant le décodage complet)
 */
export const analyzeRegionForMarker = (
  imageData: ImageData,
  x: number,
  y: number,
  size: number
): MarkerCandidate => {
  // Compter les pixels sombres dans la région
  let darkPixels = 0;
  let totalPixels = 0;
  const threshold = 80;

  const endX = Math.min(Math.floor(x + size), imageData.width);
  const endY = Math.min(Math.floor(y + size), imageData.height);

  for (let py = Math.max(0, Math.floor(y)); py < endY; py++) {
    for (let px = Math.max(0, Math.floor(x)); px < endX; px++) {
      const idx = (py * imageData.width + px) * 4;
      const r = imageData.data[idx];
      const g = imageData.data[idx + 1];
      const b = imageData.data[idx + 2];
      const gray = (r + g + b) / 3;

      totalPixels++;
      if (gray < threshold) {
        darkPixels++;
      }
    }
  }

  if (totalPixels === 0) {
    return { x, y, size, isCandidate: false };
  }

  const ratio = darkPixels / totalPixels;

  // Un marqueur ArUco a entre 20% et 80% de pixels noirs
  // (bordure noire + pattern mixte intérieur)
  // Seuils élargis pour meilleure détection
  const isCandidate = ratio > 0.20 && ratio < 0.80;

  return { x, y, size, isCandidate };
};

/**
 * Obtenir le meilleur marqueur d'une liste (confiance max)
 */
export const getBest = (markers: ArucoMarker[]): ArucoMarker | null => {
  if (markers.length === 0) return null;

  return markers.reduce((best, current) =>
    current.confidence > best.confidence ? current : best
  );
};

/**
 * Valider la géométrie des marqueurs détectés
 */
export const validateGeometry = (markers: {
  topLeft: ArucoMarker | null;
  topRight: ArucoMarker | null;
  bottomLeft: ArucoMarker | null;
  bottomRight: ArucoMarker | null;
}): boolean => {
  const { topLeft, topRight, bottomLeft, bottomRight } = markers;

  // Au moins 3 marqueurs détectés
  const detectedCount = [topLeft, topRight, bottomLeft, bottomRight].filter(
    m => m !== null
  ).length;
  if (detectedCount < 3) return false;

  if (topLeft && topRight) {
    // TL doit être à gauche de TR
    if (topLeft.centerX >= topRight.centerX) return false;
    // TL et TR doivent être à peu près à la même hauteur
    const yDiff = Math.abs(topLeft.centerY - topRight.centerY);
    const xDist = topRight.centerX - topLeft.centerX;
    if (yDiff > xDist * 0.3) return false; // Max 30% d'inclinaison
  }

  if (bottomLeft && bottomRight) {
    // BL doit être à gauche de BR
    if (bottomLeft.centerX >= bottomRight.centerX) return false;
  }

  if (topLeft && bottomLeft) {
    // TL doit être au-dessus de BL
    if (topLeft.centerY >= bottomLeft.centerY) return false;
  }

  if (topRight && bottomRight) {
    // TR doit être au-dessus de BR
    if (topRight.centerY >= bottomRight.centerY) return false;
  }

  // Vérifier que les 4 coins forment un quadrilatère convexe
  if (topLeft && topRight && bottomLeft && bottomRight) {
    return isConvexQuadrilateral([topLeft, topRight, bottomRight, bottomLeft]);
  }

  return true;
};

/**
 * Détecter tous les marqueurs ArUco dans une image avec balayage complet multi-échelle
 */
export const detectAllArucoMarkers = (
  imageData: ImageData
): ArucoMarker[] => {
  const detectedMarkers: ArucoMarker[] = [];
  let candidatesChecked = 0;
  let candidatesDecoded = 0;

  // Taille attendue d'un marqueur (environ 10mm sur une feuille A4)
  // À distance typique de scan, cela représente ~5-20% de la hauteur
  const minMarkerSize = Math.min(imageData.width, imageData.height) * 0.08;
  const maxMarkerSize = Math.min(imageData.width, imageData.height) * 0.20;

  // Multi-échelle: RÉDUIRE le nombre de tailles pour plus de performance
  const searchSizes = [
    minMarkerSize * 1.2,
    minMarkerSize * 1.8,
  ];

  console.log('🔎 Paramètres de balayage:', {
    imageSize: `${imageData.width}x${imageData.height}`,
    searchSizes: searchSizes.map(s => Math.round(s)),
    minMarkerSize: Math.round(minMarkerSize),
    maxMarkerSize: Math.round(maxMarkerSize)
  });

  for (const searchSize of searchSizes) {
    // Step size avec plus grand pas pour performance (75% au lieu de 50%)
    const stepSize = searchSize * 0.75;

    for (
      let y = 0;
      y < imageData.height - searchSize;
      y += stepSize
    ) {
      for (
        let x = 0;
        x < imageData.width - searchSize;
        x += stepSize
      ) {
        candidatesChecked++;

        // Pré-filtrage rapide
        const candidate = analyzeRegionForMarker(imageData, x, y, searchSize);

        if (candidate.isCandidate) {
          candidatesDecoded++;

          // Décodage complet du marqueur
          const marker = decodeArucoMarker(
            imageData,
            candidate.x,
            candidate.y,
            candidate.size
          );

          if (marker !== null) {
            // Éviter les doublons (même marqueur détecté à différentes échelles)
            const isDuplicate = detectedMarkers.some(
              existing =>
                existing.id === marker.id &&
                Math.abs(existing.centerX - marker.centerX) < searchSize * 0.3 &&
                Math.abs(existing.centerY - marker.centerY) < searchSize * 0.3
            );

            if (!isDuplicate) {
              console.log(`✓ Marqueur ID=${marker.id} trouvé à (${Math.round(marker.centerX)}, ${Math.round(marker.centerY)}), conf=${marker.confidence.toFixed(2)}`);
              detectedMarkers.push(marker);
            }
          }
        }
      }
    }
  }

  console.log(`📊 Stats: ${candidatesChecked} régions vérifiées, ${candidatesDecoded} décodées, ${detectedMarkers.length} marqueurs trouvés`);

  return detectedMarkers;
};

/**
 * Valider et organiser les marqueurs détectés par ID
 */
export const validateDetectedMarkers = (
  markers: ArucoMarker[]
): {
  topLeft: ArucoMarker | null;
  topRight: ArucoMarker | null;
  bottomLeft: ArucoMarker | null;
  bottomRight: ArucoMarker | null;
  isValid: boolean;
} => {
  // Grouper par ID
  const markersByID = {
    0: markers.filter(m => m.id === 0), // TL
    1: markers.filter(m => m.id === 1), // TR
    2: markers.filter(m => m.id === 2), // BL
    3: markers.filter(m => m.id === 3), // BR
  };

  // Garder seulement le meilleur de chaque ID (confiance max)
  const bestMarkers = {
    topLeft: getBest(markersByID[0]),
    topRight: getBest(markersByID[1]),
    bottomLeft: getBest(markersByID[2]),
    bottomRight: getBest(markersByID[3]),
  };

  // Vérifier la cohérence géométrique
  const isValid = validateGeometry(bestMarkers);

  return { ...bestMarkers, isValid };
};
